import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '@/core/services/open-api/user.service';
import { CurrentUser } from '@/core/models/user';
import { IntegrationConfig } from '@/core/models/IntegrationEntity';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { LoadingService } from '@/core/services/loading.service';
import { IntegrationCardData } from '@/home/settings/integrations/integration-card/integration-card.types';
import { Subscription } from 'rxjs';
import { IntegrationCardService } from '@/home/settings/integrations/integration-services/integration-card.service';

/**
 * @class IntegrationsComponent
 * @description New component for managing integrations.
 */
@Component({
    selector: 'app-integrations',
    templateUrl: './integrations.component.html',
    standalone: false,
})
export class IntegrationsComponent implements OnInit, OnDestroy {
    public apiIntegrations: IntegrationConfig[] = [];
    public availableIntegrations: IntegrationCardData[] = [];
    public activeIntegrations: IntegrationCardData[] = [];
    public filteredAvailableIntegrations: IntegrationCardData[] = [];
    public filteredActiveIntegrations: IntegrationCardData[] = [];
    public currentUser?: CurrentUser;
    public isAdmin: boolean = false;
    public searchTerm: string = '';
    public selectedIntegration?: IntegrationCardData;
    public initialized = false;
    private initMgr: InitializationManager;
    private subscriptions = new Subscription();

    constructor(
        public readonly userService: UserService,
        private readonly integrationService: IntegrationService,
        private readonly loadingService: LoadingService,
        private readonly integrationCardService: IntegrationCardService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
    }

    public ngOnInit(): void {
        this.initMgr
            // ensure that translations are loaded:
            .addDependency(this.integrationService.integrations$, {
                name: 'integrations$',
                callback: (integrations) => {
                    this.apiIntegrations = integrations;
                },
            })
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (currentUser) => {
                    this.currentUser = currentUser;
                    this.isAdmin = currentUser.isAdmin();
                },
            })
            .onInit(async ([_integrations, _currentUser]) => {
                await this.buildIntegrationLists();
                this.subscriptions.add(
                    this.activatedRoute.paramMap.subscribe((params) => {
                        const integrationParam = params.get('integration');
                        if (integrationParam) {
                            const allIntegrationCards = [...this.availableIntegrations, ...this.activeIntegrations];
                            const card = allIntegrationCards.find((i) => i.serviceId === integrationParam);
                            this.selectedIntegration = card;
                        } else {
                            this.selectedIntegration = undefined;
                        }
                        this.initialized = true;
                    }),
                );
            })
            .onChange(async ([_integrations, _currentUser]) => {
                await this.buildIntegrationLists();
                this.updateSelectedIntegration();
            })
            .initialize();
    }

    public ngOnDestroy(): void {
        this.initMgr.destroy();
        this.subscriptions.unsubscribe();
    }

    private updateSelectedIntegration(): void {
        if (this.selectedIntegration) {
            const allIntegrationCards = [...this.availableIntegrations, ...this.activeIntegrations];
            const card = allIntegrationCards.find((i) => i.serviceId === this.selectedIntegration?.serviceId);
            if (card) {
                this.selectedIntegration = card;
            }
        }
    }

    private async buildIntegrationLists(): Promise<void> {
        const allCards = await this.integrationCardService.getIntegrationCards(this.apiIntegrations);
        const activeIntegrations = allCards.filter((i) => i.integration && i.integration.status === 'active');
        const availableIntegrations = allCards.filter((i) => !i.integration || i.integration.status === 'available');
        this.activeIntegrations = activeIntegrations;
        this.availableIntegrations = availableIntegrations;
        this.filterIntegrations();
    }

    public filterIntegrations(): void {
        const searchTerm = this.searchTerm.trim().toLowerCase();
        if (searchTerm === '') {
            this.filteredAvailableIntegrations = [...this.availableIntegrations];
            this.filteredActiveIntegrations = [...this.activeIntegrations];
            return;
        }
        const filteredAvailableIntegrations = this.availableIntegrations.filter((integration) =>
            integration.searchText.includes(searchTerm),
        );
        const filteredActiveIntegrations = this.activeIntegrations.filter((integration) =>
            integration.searchText.includes(searchTerm),
        );
        this.filteredAvailableIntegrations = filteredAvailableIntegrations;
        this.filteredActiveIntegrations = filteredActiveIntegrations;
    }

    public openIntegrationDetails(serviceId: string): void {
        void this.router.navigate(['/settings/integrations', serviceId]);
    }

    public onConnect(_event: IntegrationConfig): void {
        // nothing to do here, reload happens automatically based on integrations$ subscription
    }

    public onDisconnect(_event: IntegrationConfig): void {
        this.selectedIntegration = undefined;
        this.openIntegrationList();
    }

    public openIntegrationList(): void {
        void this.router.navigate(['/settings/integrations']);
    }
}
